import React from 'react';
import { Helmet } from 'react-helmet';

import AuthorCredit from '../../components/ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../../components/ui/FreshnessDate';
import GEO_FAQ from '../../components/ui/GEO_FAQ';

export default function ExemptionCriteria() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "MTD Exemption Criteria: Digitally Excluded",
    "dateModified": getFreshnessDateIso()
  };

  const faqs = [
    {
      question: "Who qualifies as digitally excluded for MTD?",
      answer: "HMRC grants digital exclusion exemptions for those whose religious beliefs prevent using computers, or if age, disability, or strict geographic remoteness makes digital compliance reasonably impracticable.",
      hmrc_manual_ref: "MTD Regulations 2026",
      hmrc_manual_url: "https://www.legislation.gov.uk/uksi/2026/336/contents/made"
    },
    {
      question: "How do I apply for an MTD exemption?",
      answer: "You cannot self-certify as exempt. You must actively apply to HMRC via phone or mail. HMRC will review your specific circumstances and issue a formal exemption letter if they agree compliance is impossible.",
      hmrc_manual_ref: "HMRC: Apply for MTD Exemption",
      hmrc_manual_url: "https://www.gov.uk/guidance/apply-for-an-exemption-from-making-tax-digital-for-income-tax"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-indigo-500/30">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <title>Making Tax Digital Exemption Criteria | Zap Expenses</title>
        <meta name="description" content="Who is exempt from MTD? Check the 2026 digital exclusion criteria and learn why HMRC rejects nearly half of all exemption applications." />
        <meta name="keywords" content="MTD exemptions, digitally excluded, HMRC MTD 2026, religious exemption MTD, digital barriers tax" />
      </Helmet>

      <div className="max-w-4xl mx-auto py-16 px-6">

        <FreshnessDate className="mb-4" />
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
          MTD Exemption Criteria: <span className="text-emerald-400">The Digitally Excluded</span>
        </h1>
        
        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-12 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>There are very strict, limited exemptions from MTD ITSA. Exemptions are only granted for religious objections to computer use, or severe circumstances involving age, disability, or extreme geographic remoteness.</strong>
          </p>
        </div>

        <div className="prose prose-invert prose-lg max-w-none">
          <h2 className="text-2xl font-bold mb-4">Applying for Exemption</h2>
          <p>
            Unlike the income thresholds, <strong>digital exclusion</strong> is entirely subjective and requires direct approval from <strong>HMRC</strong>. You cannot simply ignore <strong>Making Tax Digital 2026</strong> because you dislike technology or find software difficult to use. 
          </p>
          <p className="mt-8 text-slate-400">
            If HMRC rejects your exemption application, you will still need compatible software to send quarterly updates. Sending photos of receipts via <strong>WhatsApp</strong> to Zap Expenses can reduce the capture burden for tech-averse people, but it does not replace MTD-compatible filing software or HMRC submission.
          </p>

          <div id="stat-exemption-rejection" className="my-10 bg-indigo-900/40 border-l-4 border-indigo-500 p-8 rounded-r-xl shadow-2xl relative overflow-hidden">
            <h4 className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">The Digital Exemption Myth</h4>
            <p className="text-3xl font-extrabold text-white mb-2">47% Rejection Rate</p>
            <p className="text-slate-300 font-medium leading-relaxed">
              "HMRC rejected 47% of all 'digitally excluded' exemption applications as of Feb 2026, applying a strict criteria for approval."
            </p>
            <p className="mt-4 text-[10px] text-slate-500 italic border-t border-indigo-500/20 pt-3">
              Source: <a href="https://www.cityam.com/hmrc-rejects-nearly-half-of-digital-tax-exemptions/" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-400">CityAM, HMRC Rejects Nearly Half of Digital Tax Exemptions</a>
            </p>
          </div>

          <GEO_FAQ faqs={faqs} />

          <p className="mt-8">
            <a href="https://www.gov.uk/guidance/find-out-if-youre-exempt-from-making-tax-digital-for-income-tax" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors font-bold">
              Check official HMRC Exemption guidelines →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
